import { useState, useEffect } from 'react';

interface Assignment {
  id: string;
  title: string;
  description: string;
  due_date: string;
  max_score: number;
  course_id: string;
  course_title: string;
  instructor_name: string;
  status: 'pending' | 'submitted' | 'graded';
  score?: number;
  submitted_at?: string;
  feedback?: string;
  is_overdue: boolean;
  submission_id?: string;
}

interface CourseWithAssignments {
  id: string;
  title: string;
  instructor_name: string;
  instructor_avatar?: string;
  assignments: Assignment[];
}

interface AssignmentsProps {
  userProfile: any;
}

export default function Assignments({ userProfile }: AssignmentsProps) {
  const [courses, setCourses] = useState<CourseWithAssignments[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submissionText, setSubmissionText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (userProfile) {
      fetchAssignments();
    }
  }, [userProfile]);

  const getAuthToken = () => {
    const session = localStorage.getItem('supabase_session');
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        return sessionData.access_token;
      } catch (e) {
        console.error('Error parsing session:', e);
      }
    }
    return import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
  };

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      
      const token = getAuthToken();
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-student-assignments?student_id=${userProfile.id}`, {
        method: 'GET',
        headers: {
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
        
        // Mở rộng tất cả khóa học có bài tập
        const courseIds = new Set(data.courses?.map((c: CourseWithAssignments) => c.id) || []);
        setExpandedCourses(courseIds);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch assignments:', response.status, errorText);
        setCourses([]);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAssignment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAssignment || !submissionText.trim()) return;

    try {
      setSubmitting(true);
      
      const token = getAuthToken();
      
      console.log('📤 Submitting assignment:', {
        assignment_id: selectedAssignment.id,
        student_id: userProfile.id,
        content_length: submissionText.length
      });
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit-assignment`, {
        method: 'POST',
        headers: {
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assignment_id: selectedAssignment.id,
          student_id: userProfile.id,
          content: submissionText
        })
      });

      console.log('📥 Response status:', response.status);
      
      const responseText = await response.text();
      console.log('📥 Response text:', responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error('❌ Failed to parse response:', e);
        throw new Error('Phản hồi từ server không hợp lệ');
      }

      if (response.ok) {
        alert('✅ Nộp bài thành công!');
        setShowSubmitModal(false);
        setSubmissionText('');
        setSelectedAssignment(null);
        fetchAssignments(); // Refresh data
      } else {
        console.error('❌ Submit failed:', responseData);
        const errorMessage = responseData.error || responseData.message || responseData.details || 'Không thể nộp bài';
        alert(`❌ Lỗi: ${errorMessage}`);
      }
    } catch (error) {
      console.error('❌ Error submitting assignment:', error);
      alert(`❌ Có lỗi xảy ra: ${error instanceof Error ? error.message : 'Vui lòng thử lại'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'graded': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Chưa nộp';
      case 'submitted': return 'Đã nộp';
      case 'graded': return 'Đã chấm';
      default: return status;
    }
  };

  const getDaysUntilDue = (dueDate: string) => {
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const toggleCourseExpansion = (courseId: string) => {
    const newExpanded = new Set(expandedCourses);
    if (newExpanded.has(courseId)) {
      newExpanded.delete(courseId);
    } else {
      newExpanded.add(courseId);
    }
    setExpandedCourses(newExpanded);
  };

  // Lọc bài tập theo trạng thái
  const getFilteredCourses = () => {
    if (filter === 'all') return courses;
    
    return courses.map(course => ({
      ...course,
      assignments: course.assignments.filter(assignment => assignment.status === filter)
    })).filter(course => course.assignments.length > 0);
  };

  const filteredCourses = getFilteredCourses();

  // Tính toán thống kê
  const allAssignments = courses.flatMap(course => course.assignments);
  const stats = {
    total: allAssignments.length,
    pending: allAssignments.filter(a => a.status === 'pending').length,
    submitted: allAssignments.filter(a => a.status === 'submitted').length,
    graded: allAssignments.filter(a => a.status === 'graded').length,
    overdue: allAssignments.filter(a => a.is_overdue).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải dữ liệu bài tập...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bài tập</h1>
          <p className="text-gray-600">Quản lý và nộp bài tập của bạn</p>
        </div>

        <button
          onClick={fetchAssignments}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Làm mới
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-file-list-line text-blue-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Tổng số</p>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-yellow-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Chưa nộp</p>
              <p className="text-xl font-bold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-send-plane-line text-blue-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Đã nộp</p>
              <p className="text-xl font-bold text-gray-900">{stats.submitted}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-green-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Đã chấm</p>
              <p className="text-xl font-bold text-gray-900">{stats.graded}</p>
            </div>
          </div>
        </div>

        {stats.overdue > 0 && (
          <div className="bg-white rounded-lg border border-red-200 p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <i className="ri-error-warning-line text-red-600"></i>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Quá hạn</p>
                <p className="text-xl font-bold text-red-600">{stats.overdue}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filter */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
            filter === 'all'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Tất cả ({stats.total})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
            filter === 'pending'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Chưa nộp ({stats.pending})
        </button>
        <button
          onClick={() => setFilter('submitted')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
            filter === 'submitted'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Đã nộp ({stats.submitted})
        </button>
        <button
          onClick={() => setFilter('graded')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
            filter === 'graded'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Đã chấm ({stats.graded})
        </button>
      </div>

      {/* Courses with Assignments */}
      <div className="space-y-4">
        {filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Không có bài tập nào</h3>
            <p className="text-gray-500">
              {filter === 'pending' 
                ? 'Bạn không có bài tập nào cần nộp.' 
                : filter === 'submitted'
                ? 'Bạn chưa nộp bài tập nào.'
                : filter === 'graded'
                ? 'Chưa có bài tập nào được chấm điểm.'
                : 'Hiện tại không có bài tập nào.'
              }
            </p>
          </div>
        ) : (
          filteredCourses.map((course) => (
            <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-200">
              {/* Course Header */}
              <div 
                className="p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleCourseExpansion(course.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-book-line text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{course.title}</h3>
                      <p className="text-sm text-gray-600">
                        <i className="ri-user-line mr-1"></i>
                        Giảng viên: {course.instructor_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {course.assignments.length} bài tập
                      </p>
                      <p className="text-xs text-gray-500">
                        {course.assignments.filter(a => a.status === 'pending').length} chưa nộp
                      </p>
                    </div>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${
                      expandedCourses.has(course.id) ? 'rotate-180' : ''
                    }`}></i>
                  </div>
                </div>
              </div>

              {/* Assignments List */}
              {expandedCourses.has(course.id) && (
                <div className="p-4 space-y-4">
                  {course.assignments.map((assignment) => {
                    const daysUntilDue = getDaysUntilDue(assignment.due_date);
                    
                    return (
                      <div key={assignment.id} className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${
                        assignment.is_overdue ? 'border-red-200 bg-red-50' : 'border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-lg font-medium text-gray-900">{assignment.title}</h4>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assignment.status)}`}>
                                {getStatusText(assignment.status)}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{assignment.description}</p>
                            
                            <div className="flex items-center space-x-6 text-sm text-gray-500">
                              <div className="flex items-center">
                                <i className="ri-calendar-line mr-1"></i>
                                <span>Hạn nộp: {new Date(assignment.due_date).toLocaleDateString('vi-VN')}</span>
                              </div>
                              <div className="flex items-center">
                                <i className="ri-star-line mr-1"></i>
                                <span>{assignment.max_score} điểm</span>
                              </div>
                              {assignment.status === 'graded' && assignment.score !== null && (
                                <div className="flex items-center">
                                  <i className="ri-medal-line mr-1"></i>
                                  <span className="font-medium">Điểm: {assignment.score}/{assignment.max_score}</span>
                                </div>
                              )}
                            </div>

                            {assignment.is_overdue && (
                              <div className="mt-2 flex items-center text-red-600 text-sm">
                                <i className="ri-error-warning-line mr-1"></i>
                                <span>Quá hạn {Math.abs(daysUntilDue)} ngày</span>
                              </div>
                            )}

                            {daysUntilDue > 0 && daysUntilDue <= 3 && assignment.status === 'pending' && (
                              <div className="mt-2 flex items-center text-yellow-600 text-sm">
                                <i className="ri-time-line mr-1"></i>
                                <span>Còn {daysUntilDue} ngày</span>
                              </div>
                            )}

                            {assignment.feedback && (
                              <div className="mt-3 bg-blue-50 p-3 rounded-lg">
                                <p className="text-sm text-blue-800">
                                  <strong>Nhận xét từ giáo viên:</strong> {assignment.feedback}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-2 ml-4">
                            {assignment.status === 'pending' && !assignment.is_overdue && (
                              <button
                                onClick={() => {
                                  setSelectedAssignment(assignment);
                                  setSubmissionText('');
                                  setShowSubmitModal(true);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                              >
                                <i className="ri-upload-line mr-1"></i>
                                Nộp bài
                              </button>
                            )}
                            
                            {assignment.status === 'graded' && assignment.score && assignment.score >= 90 && (
                              <div className="flex items-center text-yellow-600" title="Bài làm xuất sắc">
                                <i className="ri-trophy-line text-xl"></i>
                              </div>
                            )}

                            {assignment.submitted_at && (
                              <div className="text-xs text-gray-500 text-right">
                                Nộp lúc:<br />
                                {new Date(assignment.submitted_at).toLocaleDateString('vi-VN', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Submit Modal */}
      {showSubmitModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Nộp bài tập
                </h3>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitAssignment} className="p-6">
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-2">{selectedAssignment.title}</h4>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Khóa học:</span>
                      <div className="font-medium">{selectedAssignment.course_title}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Điểm tối đa:</span>
                      <div className="font-medium">{selectedAssignment.max_score} điểm</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Hạn nộp:</span>
                      <div className="font-medium">{new Date(selectedAssignment.due_date).toLocaleDateString('vi-VN')}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Giảng viên:</span>
                      <div className="font-medium">{selectedAssignment.instructor_name}</div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h5 className="font-medium text-gray-900 mb-2">Mô tả bài tập:</h5>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedAssignment.description}</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nội dung bài làm *
                </label>
                <textarea
                  value={submissionText}
                  onChange={(e) => setSubmissionText(e.target.value)}
                  rows={8}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập nội dung bài làm của bạn..."
                  maxLength={5000}
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {submissionText.length}/5000 ký tự
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all whitespace-nowrap"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={!submissionText.trim() || submitting}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Đang nộp...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Nộp bài
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
